/*
 * Common product definitions for ecommerce.
 *
 * IMPORTANT NOTE: This file should be edited in the `web` repo only! Edit in
 * `web` and then run `yarn product:types` in the `ecommerce` repository to sync types.
 *
 * This file is copied verbatim to the ecommerce repo via `yarn product:types`, so it must not contain any
 * imports.
 */

/**
 * Configured features in GrowthBook
 * See https://app.growthbook.io/features
 */
export type GrowthBookFeatureName =
  | 'dunning_preauth_ab_test'
  | 'makers-collar-kits'
  | 'vet-suggestions-enabled'
  | 'in-app-supplement-management'
  | 'ecom-rounded-pricing'
  | 'community-revamp-enabled'
  | 'android-community-revamp-enabled'
  | 'supplements-five-dollar-first-bag'
  | 'collar-tariff-fee-sku'
  | 'post-purchase-sms'
  | 'ecom-supported-countries'
  | 'ecom-supported-languages'
  | 'enable-live-activity-walk-notifications'
  | 'ecom-bfcm-upgrade'
  | 'ecom-bfcm-s3-s3plus-coupon-code'
  | 'free-trial-enabled'
  | 'hardware-first-checkout-2'
  | 'multipet-position-discount'
  | 'escape-cascade-shadow';

export enum SupplementType {
  CALMING = 'calming',
  EIGHT_IN_ONE = 'eightInOne',
  HIP_AND_JOINT = 'hipAndJoint',
  SKIN_AND_COAT = 'skinAndCoat',
}

export enum ThirdPartyId {
  AIRVET = 'AIRVET',
  COSTCO_COM = 'COSTCO_COM',
  VERIZON = 'VERIZON',
}

export const series1CollarId = 'smart-collar';
export const series2CollarId = 'smart-collar-v2';
export const series3CollarId = 'smart-collar-v3';
export const series3PlusCollarId = 'smart-collar-v3b';
export const miniCollarId = 'smart-collar-mini';
export const ultraCollarId = 'smart-collar-ultra';

export const collarProductIds = [
  series1CollarId,
  series2CollarId,
  series3CollarId,
  series3PlusCollarId,
  miniCollarId,
  ultraCollarId,
] as const;
export type CollarProductId = (typeof collarProductIds)[number];

export function productIdIsCollar(productId: string): productId is CollarProductId {
  return collarProductIds.includes(productId as CollarProductId);
}

export const f1BandId = 'additional-band';
export const f3BandId = 'additional-band-s3';
export const martingaleBandId = 'martingale-band';

export const f3EndlinkId = 'endlinks-f3';
export const f3EndlinkSmallId = 'endlinks-f3-small';

/**
 * sku prefix for a Series 3 collar kit with a maker's band
 */
export const f3MakerKitSkuBase = 'F3-MBKUA';
export const f3MakerBandSkuBase = 'F3-K';

/**
 * sku for a Series 3 collar kit box that contains only a module and charging base (no band)
 */
export const series3BaseModuleKitSku = 'F3-MBUA';

export const giftWrapSkuCode = 'W';
export const sizeLabelSkuCode = 'L';
export const series3GiftWrapSkuBase = 'F3W-MBCUA';
export const series3SizeLabelSkuBase = 'F3L-MBCUA';

/**
 * sku for a Series 3+ collar kit box
 */

export const series3PlusBaseModuleKitSku = 'F3B-MBUA';

/**
 * sku for an international Series 3+ collar kit box (module + charging base, no band). Customers outside US/CA
 * receive these via the S3+ collar kit's country-routing fulfillment rules.
 */
export const series3PlusInternationalBaseModuleKitSku = 'F3C-MBUA';

/**
 * The `subItemCodes` property on products are used by the returns tracker to help determine what individual components
 * are part of the product when making a warranty exchange.
 *
 * M = module
 * B = charging base
 * C = fi band
 * K = makers band
 * U = usb cord
 * A = a/c adapter
 *
 * A Fi collar kit would have subitem codes M, B, C, U, and A. Using this information, when creating an RMA (return
 * merchandise authorization) a CX (customer experience) agent might want to only exchange one or more of these
 * individual components depending on the problem the customer is having e.g. if the band doesn't fit, but the module
 * and charing base work fine, then we would rather just replace the band ('C' subitem) instead of replacing the entire
 * collar kit.
 *
 * TODO: Convert these to enums (https://app.asana.com/0/1202414697517517/1205187196777579/f)
 */
export type ReturnsTrackerBandSubItemCode = 'C' | 'K';
export type ReturnsTrackerSubItemCode = ReturnsTrackerBandSubItemCode | 'M' | 'B' | 'U' | 'A';

/**
 * The billing cadence for a subscription. This is used to distinguish between yearly and monthly
 * pricing schemes. This is different from the billing *period*: a 2 year plan has a billing period
 * of 2 years but a cadence of Year.
 */
export enum BillingCadence {
  Month = 'month',
  Year = 'year',
  Week = 'week',
}

/**
 * The tender determines how the user paid / will pay. Currently it's only used to flag certain tenders as invalid for use on products.
 *
 * Aliased to the corresponding GQL type for consistency.
 */

export enum TenderType {
  CreditCard = 'CreditCard',
  PayPal = 'PayPal',
}

export type ProductID = string;
export type IProduct =
  | IBandProduct
  | IChargingBaseProduct
  | ICollarKitProduct
  | IEndlinkProduct
  | IGiftCardProduct
  | IModuleProduct
  | IMiniProduct
  | IMiniAttachmentProduct
  | IUltraProduct
  | INanoProduct
  | IS2UpgradeProduct
  | IS2UpgradeInsertProduct
  | ISubscriptionProduct
  | ISupplementSubscriptionProduct
  | ISupplementProduct
  | I3rdPartySubscriptionProduct
  | IEarlyRenewalProduct;

export enum ProductLabel {
  Series2KitOrModule = 'Series2KitOrModule',
  ReturnRefundEligible = 'ReturnEligible',
  /**
   * A discontinued product may no longer be RMA'd by itself, for example a series 1 kit.
   */
  Discontinued = 'Discontinued',
  /**
   * A product that's no longer manufactured, so we wouldn't be able to fulfill an order for it.
   */
  NoLongerManufactured = 'NoLongerManufactured',
  /**
   * A product that has a serial number e.g. FB11K44001, FC21K79010, FN1201H47562
   */
  Serialized = 'Serialized',
  /**
   * A product that cannot be added to an RMA for return or replacement
   */
  IneligibleForRma = 'IneligibleForRma',
  /**
   * By default, customers are instructed to dispose of their product on a warranty exchange. This label
   * indicates the product should be flagged for returning to us instead.
   */
  ReturnProductOnExchange = 'ReturnProductOnExchange',
}

/**
 * Regions where a product can be sold. Values should match the ISO-2 code for the region.
 * This enum should be kept in sync with the GQL enum FiCountry; it needs to be a separate entry because the type file
 * is copied into the ecommerce repo.
 * */
export enum ProductRegion {
  /** United States of America */
  US = 'US',
  /** Canada */
  CA = 'CA',
  /** United Kingdom */
  GB = 'GB',
  /** France */
  FR = 'FR',
  /** Germany */
  DE = 'DE',
  /** Spain */
  ES = 'ES',
  /** Finland */
  FI = 'FI',
  /** Malta */
  MT = 'MT',
  /** Romania */
  RO = 'RO',
  /** Austria */
  AT = 'AT',
  /** Netherlands */
  NL = 'NL',
  /** Slovenia */
  SI = 'SI',
  /** Latvia */
  LV = 'LV',
  /** Slovakia */
  SK = 'SK',
  /** Belgium */
  BE = 'BE',
  /** Luxembourg */
  LU = 'LU',
  /** Liechtenstein */
  LI = 'LI',
  /** Sweden */
  SE = 'SE',
  /** Denmark */
  DK = 'DK',
  /** Norway */
  NO = 'NO',
  /** Switzerland */
  CH = 'CH',
  /** Iceland */
  IS = 'IS',
  /** Turkey */
  TR = 'TR',
}

export interface PricingByCurrency {
  USD: number;
  CAD?: number | null;
  GBP?: number | null;
  EUR?: number | null;
  RON?: number | null;
  SEK?: number | null;
  DKK?: number | null;
  NOK?: number | null;
  CHF?: number | null;
  ISK?: number | null;
  TRY?: number | null;
}

/**
 * Price segments allow us to offer different prices for the same subscription plan without managing multiple
 * subscription plan codes, which presents challenges when it comes to things like coupons. Segments are branched
 * off a specific currency. Each segment has a code and price.
 *
 *                       Plan Code
 *                           │
 *          ┌────────────────┼────────────────┐
 *          ▼                ▼                ▼
 *         USD              CAD              EUR
 *          │                │                │
 *     ┌────┴────┐      ┌────┴────┐      ┌────┼────┐
 *     ▼         ▼      ▼         ▼      ▼    ▼    ▼
 *  Default    A/B   Default    A/B   Default FR   DE
 *
 * In this illustration, each Default price will be defined in the product's `pricingInCents` field with the A/B
 * test prices or region-specific (e.g. FR, DE) prices defined in the optional `priceSegmentsByCurrency` field.
 *
 * There are two types of price segments for our different use cases:
 * - Region: The price segment is only available in a specific country.
 *       - This is useful when we want to offer a different price for different countries that use the same currency
 *         (e.g. EUR in Italy vs. EUR in Germany)
 * - Experiment: The price segment is only available to users who are enrolled in a Growthbook experiment.
 *       - This is useful when we want to A/B test different prices for the same subscription plan.
 *       - Experiments can optionally be scoped to a specific country as well.
 */

/**
 * Each segment has at minimum a code and price.
 */
interface PriceSegmentBase {
  segmentCode: string;
  priceInCents: number;
}

/**
 * Segments for A/B tests need to specify the Growthbook feature flag that is required to be enrolled in to receive the
 * price segment. Optionally, the segment can be scoped to a specific country.
 */
interface PriceSegmentExperiment extends PriceSegmentBase {
  kind: 'experiment';
  requiresExperimentEnrollment: GrowthBookFeatureName;
  countryCode?: string;
}

/**
 * Segments for region-specific pricing need to specify the country code that the segment is available in.
 * This would be the default for the specific country, but if an experiment is available in the country then experiment
 * price segment will be used if the user is enrolled in the experiment.
 */
interface PriceSegmentRegion extends PriceSegmentBase {
  kind: 'region';
  countryCode: string;
}

type PriceSegment = PriceSegmentExperiment | PriceSegmentRegion;

/**
 * The inputs we'd need to determine if a price segment is applicable for a given user.
 * Every user will have a country code. Not every user will be enrolled in an experiment.
 */
export interface PriceSegmentInput {
  countryCode: string;
  growthBookEnrollments?: Set<GrowthBookFeatureName>;
}

/**
 * Price segments are grouped by currency. Each currency can have multiple price segments.
 */
export interface PriceSegmentsByCurrency {
  USD?: PriceSegment[];
  CAD?: PriceSegment[];
  GBP?: PriceSegment[];
  EUR?: PriceSegment[];
  RON?: PriceSegment[];
  SEK?: PriceSegment[];
  DKK?: PriceSegment[];
  NOK?: PriceSegment[];
  CHF?: PriceSegment[];
  ISK?: PriceSegment[];
  TRY?: PriceSegment[];
}

interface IBaseProduct {
  id: ProductID;
  name: string;
  /** @deprecated use `pricingInCents` instead */
  priceInCents: number;
  /** Pricing in our supported currencies */
  pricingInCents: PricingByCurrency;
  /**
   * For a given currency, `pricingInCents` will provide the default price for
   * that currency. Price segments can be used to define alternate prices for A/B
   * tests or segment pricing by regions for a given currency (e.g. we could use
   * different prices in France vs. Germany for EUR)
   */
  priceSegmentsByCurrency?: PriceSegmentsByCurrency;
  /**
   * Ships free at any cart value, exempting the cart it's in from
   * `FREE_SHIPPING_THRESHOLD_BY_CURRENCY`. Declared per-product rather than derived from category
   * so this file stays the single source of the policy: the storefront reads this field off
   * `GET /products` instead of re-deciding which products qualify.
   */
  shipsFree?: boolean;
  description: string;
  /**
   * Whether this product is shown as the front-page item.
   */
  featured?: boolean;
  /**
   * If this is a string, it defines a coupon name prefix for coupons that
   * may be applied to this product.
   */
  couponEligible?: boolean | string[];
  exchangeOnly?: boolean;

  /**
   * @deprecated Use `physical` instead.
   */
  physical: boolean;

  unavailableForTenders?: Array<TenderType>;
  labels?: ProductLabel[];
  subItemCodes?: ReturnsTrackerSubItemCode[];
  discountedFromOriginalPriceInCents?: number;
  category: ProductCategory;
  rmaProductCategory?: RMAProductCategory;
  thirdPartyPriceInCents?: Partial<Record<ThirdPartyId, number>>;
  regionsAvailableForPurchase: ProductRegion[];
  /**
   * Ordered list of rules that transform this product's ordered SKU into the SKU we actually ship. Applied to
   * the product's base SKU; variant-level rules (on `IBaseVariant.fulfillmentRules`) take precedence for variant
   * SKUs. First matching rule wins; no match falls back to shipping the ordered SKU as-is.
   */
  fulfillmentRules?: FulfillmentRule[];
  /**
   * Bill-of-materials for SKUs eligible for JIT (just-in-time) kitting, keyed by output SKU. An entry's presence
   * marks that SKU as JIT-eligible; the array lists the component SKUs the warehouse must have on hand to
   * assemble the kit at shipment time. Whether the component-level inventory check actually runs at fulfillment
   * time is controlled by the `InventoryConfiguration.justInTimeKittingEnabled` toggle per SKU + warehouse.
   *
   * For products with variants, prefer the variant-level `kitComponents` so the bill-of-materials lives next to
   * the variant that owns the SKU. Variant-level entries take precedence over product-level for variant SKUs.
   */
  kitComponents?: Record<string, string[]>;
}

/**
 * The SKU we tell the warehouse to ship for a matched fulfillment rule.
 */
export interface FulfillmentRuleOutput {
  sku: string;
}

/**
 * Matchers for `FulfillmentRule`. All defined matchers are combined with AND; omitted matchers are treated as
 * "don't care". Multiple rules with the same output express disjunctions.
 *
 * Matchers describe external conditions (shipping destination, order source) — not attributes of the SKU or
 * variant itself. Attributes like color, size, or gift-wrap status are implicit in *where* the rule is
 * attached: variant-level rules apply only to that variant, product-level rules apply to the product's base
 * SKU.
 *
 * Note on typing: `orderSource` is declared as `string` rather than the `OrderSource` enum because this file
 * is copied to the ecommerce repo via `yarn product:types` and cannot import from other files. Rule authors
 * should pass the `OrderSource` enum values directly — TypeScript accepts them as strings.
 */
export interface FulfillmentRuleMatchers {
  /** Matches when the shipping country is in this set (case-insensitive). */
  country?: Set<string>;
  /** Matches when the shipping country is NOT in this set — used to express "anywhere except …". */
  excludedCountries?: Set<string>;
  /** OrderSource enum value as a string literal (e.g., `OrderSource.Internal`). */
  orderSource?: string;
  thirdPartyId?: ThirdPartyId;
  /**
   * Matches when the order contains ≥2 line items with the same color option but different sizes. Encodes
   * the "put a size label on this kit so the customer can tell boxes apart" rule. Requires the input to
   * include a variant; otherwise the rule does not match.
   */
  sameColorDifferentSizesInOrder?: boolean;
  /** Matches APO/FPO/DPO addresses (country=US with a state in ARMED_FORCES_STATES). */
  isArmedForces?: boolean;
  /** Matches US territory addresses (country=US with a state in US_TERRITORY_STATES, e.g. PR, VI). */
  isUsTerritory?: boolean;
  /**
   * Parsed from prepaid SKU suffix `/-([A-Z]?)(\d{2})M$/`. The numeric term length in months (6, 12, 24).
   * Undefined when the SKU has no prepaid suffix.
   */
  prepaidRenewalMonths?: number;
  /**
   * Optional alphabetic code parsed from prepaid SKU suffix `/-([A-Z]?)(\d{2})M$/`.
   *
   * Historically this was described as a sales-channel letter (for example `F` for Fi retail, `C` for
   * Costco, `A` for Amazon), but some prepaid suffixes use this slot for other SKU variants such as
   * gift-wrap (`W`). Undefined only when the prepaid suffix has no alphabetic code.
   */
  prepaidChannelCode?: string;
  /** Matches when the line item is part of a free-trial subscription. */
  freeTrial?: boolean;
  /** Matches when the order's channel is the employee gifting program. */
  employeeGift?: boolean;
  /**
   * Matches when a refurb grade was requested (parsed from a Recurly warranty-exchange refurb SKU) and it
   * will accept this grade as an output — i.e. the requested grade is this grade or a less-preferred one
   * (A best, then B, then C). So `refurbStockAtLeast: 'A'` matches requests A and B; `'B'` matches only B.
   * Ordering B-tier rules before A-tier rules yields the B→A→New fallback without explicit fallback rules.
   * `'A' | 'B' | 'C'` inline rather than importing `RefurbStock` because this file is copied to ecommerce.
   */
  refurbStockAtLeast?: 'A' | 'B' | 'C';
  /**
   * Closure that evaluates a feature flag at match time. Lets a rule gate itself on a flag without
   * forcing the rule engine to know about every flag. The closure should be a thin wrapper around the
   * flag-directory lookup, e.g. `() => flagDirectory.ecommerce.fooEnabled()`.
   */
  featureFlag?: () => boolean;
}

/**
 * A declarative rule mapping a line item's context (country, order source, variant, etc.) to the SKU we ship
 * plus any JIT (just-in-time) inventory-check components. Stored as plain data so product configs — including
 * the ecommerce repo copy of this file — can declare rules without depending on backend-only matching logic.
 */
export interface FulfillmentRule {
  output: FulfillmentRuleOutput;
  matchers?: FulfillmentRuleMatchers;
  /**
   * When true, the rule only matches if `output.sku` is currently in stock somewhere — letting a rule
   * "fall through" to the next when its preferred SKU is unavailable. Inventory is evaluated in the
   * resolver via its required `isOutputInStock` callback.
   */
  requireInStock?: boolean;
}

export enum ProductCategory {
  COLLAR_KIT,
  MODULE,
  BAND,
  CHARGING_BASE,
  GIFT_CARD,
  SUBSCRIPTION,
  ENDLINK,
  MICROCHIP,
  S2_UPGRADE,
  SUPPLEMENT,
  THIRD_PARTY_MEMBERSHIP,
  EARLY_RENEWAL,
  MINI,
  MINI_ATTACHMENT,
  ULTRA,
}

export enum RMAProductCategory {
  COLLAR_SERIES_1_OR_2 = 'collar_series_1_or_2',
  COLLAR_SERIES_3 = 'collar_series_3',
  COLLAR_SERIES_3_PLUS = 'collar_series_3_plus',
  NANO = 'nano',
  SUPPLEMENTS = 'supplements',
  MINI_SERIES_1 = 'mini_series_1',
  ULTRA_SERIES_1 = 'ultra_series_1',
}

// Used to determine which SKUs we should report inventory for on the third party's website. This can be separate
// from the discontinued flag on the variant since it's possible that we want to discontinue selling a SKU
// on the third party's site but continue to sell it on our own site.
export enum ThirdPartyAvailabilityStatus {
  // Available to be sold on the third party's website.
  Available = 'available',
  // Was one time available to be sold on the third party's website but is now discontinued
  Discontinued = 'discontinued',
  // Never available to be sold on the third party's website
  Unavailable = 'unavailable',
}

export type ThirdPartyAvailability =
  | {
      status: ThirdPartyAvailabilityStatus.Available | ThirdPartyAvailabilityStatus.Unavailable;
    }
  | {
      status: ThirdPartyAvailabilityStatus.Discontinued;
      discontinuedDate: Date;
    };

interface ISimpleProduct extends IBaseProduct {
  sku: string;
  weightInOunces?: number;
  /**
   * This attribute is used to determine the cost of goods to split shipments when they reach a max value
   */
  factoryCostInDollars?: number;
}

export interface IEndlinkProduct extends ISimpleProduct {
  bandSeries: BandSeries;
  size?: string;
  physical: true;
  category: ProductCategory.ENDLINK;
  rmaProductCategory: RMAProductCategory.COLLAR_SERIES_1_OR_2 | RMAProductCategory.COLLAR_SERIES_3;
}

export interface IChargingBaseProduct extends ISimpleProduct {
  physical: true;
  category: ProductCategory.CHARGING_BASE;
  rmaProductCategory:
    | RMAProductCategory.COLLAR_SERIES_1_OR_2
    | RMAProductCategory.COLLAR_SERIES_3
    | RMAProductCategory.COLLAR_SERIES_3_PLUS;
}

export interface INanoProduct extends ISimpleProduct {
  physical: true;
  category: ProductCategory.MICROCHIP;
  rmaProductCategory: RMAProductCategory.NANO;
}

export interface IModuleProduct extends ISimpleProduct {
  physical: true;
  category: ProductCategory.MODULE;
  rmaProductCategory:
    | RMAProductCategory.COLLAR_SERIES_1_OR_2
    | RMAProductCategory.COLLAR_SERIES_3
    | RMAProductCategory.COLLAR_SERIES_3_PLUS;
}

export interface RetailSku {
  sku: string;
  /**
   * Number of membership months that a customer purchases for this SKU
   */
  renewalMonths: number;
}
export interface IMiniProduct extends ISimpleProduct {
  id: CollarProductId;
  physical: true;
  category: ProductCategory.MINI;
  rmaProductCategory: RMAProductCategory.MINI_SERIES_1;
  kitSkuToShip: string;
  thirdPartyKitSku: Partial<Record<ThirdPartyId, string>>;
  thirdPartyAvailability?: {
    costco?: ThirdPartyAvailability;
  };
  /**
   * Options for buying a mini collar kit with a deferred subscription, meaning we aren't immediately creating a
   * subscription under the purchaser's account and instead the subscription will be created under a recipient's
   * account at a later date.
   */
  deferredSubscriptionOptions?: DeferredSubscriptionOption[];
  /**
   * Special SKUs for the mini collar kit that we will be sending to retail partners.
   * We won't be selling these SKUs on our own ecomm site, but we need them defined
   * so we can sync any Recurly invoices that might contain them. Finance + ops uses
   * a manually created Recurly invoice to send the kits to retail partners.
   */
  retailSkus?: RetailSku[];
}

export interface IMiniAttachmentProduct extends ISimpleProduct {
  physical: true;
  category: ProductCategory.MINI_ATTACHMENT;
  rmaProductCategory: RMAProductCategory.MINI_SERIES_1;
}

export interface IUltraProduct extends ISimpleProduct {
  id: CollarProductId;
  physical: true;
  category: ProductCategory.ULTRA;
  rmaProductCategory: RMAProductCategory.ULTRA_SERIES_1;
  /**
   * Ultra is a special case where it can be added as a secondary device (in combination with an S3, S3+, or Mini),
   * or it can be purchased as a standalone product with its own subscription. We want to charge different prices
   * for these two cases.
   */
  standalonePricingInCents: PricingByCurrency;
}

export interface IS2UpgradeInsertProduct extends ISimpleProduct {
  physical: true;
  category: ProductCategory.S2_UPGRADE;
}

export interface IS2UpgradeProduct extends ISimpleProduct {
  physical: false;
  category: ProductCategory.S2_UPGRADE;
}

/**
 * Fi makes our own bands for collars, but third-party makers are also able to make bands using Fi endlinks.
 */
export enum BandMaker {
  Fi = 'Fi',
  // Third-party makers
  LandsharkSupply = 'Landshark Supply',
  MimiGreen = 'Mimi Green',
  RopeHounds = 'Rope Hounds',
  StuntPuppy = 'Stunt Puppy',
  ZeeDog = 'Zee.Dog',
}

/**
 * A product could have many "variants" representing skus for all of the individual options the customer can choose
 * from when purchasing that product. For example, a collar kit product will have a variant for each color/size
 * combination that is available for that product.
 */
interface IBaseVariant {
  sku: string;
  options: IVariantOptions;
  weightInOunces?: number;
  factoryCostInDollars?: number;
  /**
   * Some variants require an upcharge on top of the base price of the product (e.g. makers band). This attribute will
   * hold the amount of that upcharge in cents.
   */
  additionalPriceInCents?: number;
  bandMaker: BandMaker;
  bandSubItemCode: ReturnsTrackerBandSubItemCode;
  /**
   * We will always want to keep all variants that we've sold sold in the product configuration for order history
   * purposes, but we will want to hide discontinued variants from UIs so they should be flagged accordingly.
   */
  discontinued?: boolean;
  /**
   * Ordered list of rules that transform this variant's ordered SKU into the SKU we actually ship.
   *
   * These rules are variant-specific overrides: when present, they are evaluated for this variant instead of the
   * product-level {@link FulfillmentRule} list. Same semantics as the product-level rules otherwise — first match
   * wins, and if no rule matches we ship the ordered SKU as-is.
   */
  fulfillmentRules?: FulfillmentRule[];
}

export type IBandVariant = IBaseVariant;

export interface ICollarKitVariant extends IBaseVariant {
  /**
   * While the `sku` attribute of a collar kit variant represents the sku for the full kit (e.g. F3-MBCUA-GY-S), the
   * `bandSku` is meant to represent the sku for only the spare band that is contained within that kit (e.g. F3-C-GY-S)
   */
  bandSku: string;
  /**
   * For variants of a collar kit that are purchased with a deferred subscription that is not created immediately.
   * An example of this is gift collar kits where the purchaser pays for the initial term of the subscription at the
   * time of purchase, but the subscription is not created until the collar is activated by the gift recipient.
   */
  deferredSubscriptionRenewalMonths?: number;
  giftWrapped?: boolean;

  thirdPartyAvailability?: {
    costco?: ThirdPartyAvailability;
  };
  /**
   * Bill-of-materials for this variant's JIT-kittable output SKUs, keyed by output SKU. An entry's presence
   * marks that SKU as JIT-eligible. Mirrors {@link IProduct.kitComponents} but scoped to a single variant —
   * preferred for collar kits because each variant's fulfillment rules can emit several output SKUs (e.g. the
   * F3B/F3C country split, gift-wrap with vs. without size label) whose components differ.
   */
  kitComponents?: Record<string, string[]>;
}

export type IVariant = IBandVariant | ICollarKitVariant;

export interface ICollarKitProduct extends IBaseProduct {
  id: CollarProductId;
  /**
   * If true, the user MUST include a bundled subscription with this product to checkout. The product cannot be
   * purchased otherwise.
   */
  bundledSubscriptionRequiredForPurchase: boolean;
  physical: true;
  category: ProductCategory.COLLAR_KIT;
  bandSeries: BandSeries;
  variants: ICollarKitVariant[];
  /**
   * Some of our collar kits will be kitted at the 3PL. We will send them the full collar kit sku and they break it
   * down into a sku for a kit with no band (the box includes only a module + charging base) and a sku for only the
   * spare band. They add the band to the box to put together the full kit at time of shipping. We will need to know
   * the sku for the kit without the band so we can check inventory accordingly.
   */
  baseModuleKitSku?: string;
  rmaProductCategory:
    | RMAProductCategory.COLLAR_SERIES_1_OR_2
    | RMAProductCategory.COLLAR_SERIES_3
    | RMAProductCategory.COLLAR_SERIES_3_PLUS;
  /**
   * Collar-kit rules always live on the variants (see `ICollarKitVariant.fulfillmentRules`). The base product
   * has nothing to map — every collar-kit ordered SKU is a variant SKU, and rule outputs are variant-specific.
   * This type-level `undefined` prevents accidentally attaching a product-level rule that would silently apply
   * to every variant.
   */
  fulfillmentRules?: undefined;
}

export interface ICollarJITKitProduct extends ICollarKitProduct {
  baseModuleKitSku: string;
}

export interface IBandProduct extends IBaseProduct {
  id: ProductID;
  physical: true;
  category: ProductCategory.BAND;
  bandSeries: BandSeries;
  variants: IBandVariant[];
  rmaProductCategory: RMAProductCategory.COLLAR_SERIES_1_OR_2 | RMAProductCategory.COLLAR_SERIES_3;
  /** Same reasoning as `ICollarKitProduct.fulfillmentRules` — band rules live on variants. */
  fulfillmentRules?: undefined;
}

export type IProductWithVariants = ICollarKitProduct | IBandProduct;
export type IProductWithoutVariants = Exclude<IProduct, IProductWithVariants>;
export type IPhysicalProduct = Exclude<IProduct, IGiftCardProduct | ISubscriptionProduct | IS2UpgradeProduct>;

export interface IGiftCardProduct extends IBaseProduct {
  sku: string;
  physical: false;
  /**
   * The name that will be used in the yellow banner that's displayed at the top
   * of the screen during the redemption code. Right now this just omits "(Gift)"
   * from the end since that looks weird.
   */
  nameForBanner: string;
  /**
   * When trying to find gift card details by looking them up by their dollar
   * amount, also consider these alternative prices to be instances of this gift card.
   */
  alternativePricesInCents?: number[];
  category: ProductCategory.GIFT_CARD;
}

export interface ISupplementSubscriptionProduct extends IBaseProduct {
  sku: string;
  /**
   * This is the sku of the product that will be shipped to the customer when they purchase this supplement.
   * It may differ from the SKU on the order line item which is more directly associated with the subscription cadence
   */
  skuToShip: string;
  physical: true;
  category: ProductCategory.SUPPLEMENT;
  supplementType: SupplementType;
  weightInOunces: number;
  factoryCostInDollars?: number;
  /** @deprecated use `activationFeePricingInCents` instead */
  activationFeeInCents?: number;
  activationFeePricingInCents?: PricingByCurrency;
  discountedFromOriginalActivationFeeInCents?: number;
  freeTrial: boolean;
  freeTrialDays?: number;
  daysBetweenShipments: number;
  weeksBetweenShipments: number;
  billingCadence: BillingCadence;
  displayedRate: string;
  displayedRateUnit: string;
  /**
   * How many billing periods are in a term? A term is the default length of time that customers are committed to a
   * subscription. e.g. a subscription with renewalMonths=1, billingPeriodsPerTerm=6 is a subscription that bills
   * once every month, and there are 6 months in a term. So the customer is committed to the subscription for 6 months.
   * e.g. a subscription with renewalMonths=12, billingPeriodsPerTerm=1 is a subscription that bills once every year
   * and there is 1 year in a term. So the customer is committed to the subscription for 1 year.
   */
  billingPeriodsPerTerm: number;
  recommendedForDogSize: SizeOption;
  recommendedForDogWeightRangePounds: WeightRange;
  // Used to display new discounted price vs old price
  originalPriceInCents?: number;
  // The product name used in customer.io event tracking, this allows for more detail than we show on ecommerce site
  customerIoName: string;
  // The product name used in the in-app subscriptions management flow
  inAppManagementName: string;
  // If this plan should be displayed in ecomm. This is false for plans without free trials.
  display: boolean;
  // Plans without free trials replace plans without free trials when a user is purchasing any supplement subscription except their first.
  replacedBySku?: string;
  savings?: string;
  rmaProductCategory: RMAProductCategory.SUPPLEMENTS;
}

export interface ISupplementProduct extends IBaseProduct {
  sku: string;
  skuToShip: string;
  physical: true;
  category: ProductCategory.SUPPLEMENT;
  supplementType: SupplementType;
  weightInOunces: number;
  factoryCostInDollars?: number;
  activationFeeInCents?: number;
  freeTrial: boolean;
  freeTrialDays?: number;
  daysBetweenShipments: number;
  weeksBetweenShipments: number;
  // Used to display new discounted price vs old price
  originalPriceInCents?: number;
  // The product name used in customer.io event tracking, this allows for more detail than we show on ecommerce site
  customerIoName: string;
  // The product name used in the in-app subscriptions management flow
  inAppManagementName: string;
  // If this plan should be displayed in ecomm. This is false for plans without free trials.
  display: boolean;
  rmaProductCategory: RMAProductCategory.SUPPLEMENTS;
}

export interface I3rdPartySubscriptionProduct extends IBaseProduct {
  sku: string;
  physical: false;
  thirdPartyId: ThirdPartyId;
  displayedRate: string;
  displayedRateUnit: string;
  /**
   * Renews after *n* months. How long is a single billing period?
   */
  renewalMonths: number;
  /**
   * How many billing periods are in a term? A term is the default length of time that customers are committed to a
   * subscription. e.g. a subscription with renewalMonths=1, billingPeriodsPerTerm=6 is a subscription that bills
   * once every month, and there are 6 months in a term. So the customer is committed to the subscription for 6 months.
   * e.g. a subscription with renewalMonths=12, billingPeriodsPerTerm=1 is a subscription that bills once every year
   * and there is 1 year in a term. So the customer is committed to the subscription for 1 year.
   */
  billingPeriodsPerTerm: number;
  freeTrial?: boolean;
  /**
   * Free trial for *n* days.
   */
  freeTrialDays?: number;
  billingCadence: BillingCadence;
  category: ProductCategory.THIRD_PARTY_MEMBERSHIP;
}

export interface ISubscriptionProduct extends IBaseProduct {
  sku: string;
  physical: false;
  displayedRate: string;
  displayedRateUnit: string;
  mostPopular?: boolean;
  freeTrial?: boolean;
  /**
   * A string depiction of the savings to be displayed, like "Save 16%"
   */
  savings?: string;
  /**
   * The amount users would save if they upgrade from a monthly subscription. Used in the monthly upgrade flow.
   */
  monthlyUpgradeSavings?: string;
  /**
   * Renews after *n* years.
   *
   * @deprecated Use `renewalMonths` instead.
   */
  renewalYears?: number;
  /**
   * Renews after *n* months. How long is a single billing period?
   */
  renewalMonths: number;
  /**
   * "Buy It" membership plans will be configured in Recurly with a billing period of 5 years and set to expire at the
   * end of that initial 5 year term instead of auto-rewnewing so we won't automatically charge them again. Then, if
   * the customer is still using the device after those 5 years and contacts support after seeing their subscription
   * has expired, a cx agent can manually turn the subscription back on for no charge. We will, therefore, be setting
   * renewalMonths to 60 for these bought plans in the backend product config which helps this type of plan to play
   * nicely with our existing grace period logic.
   *
   * Since we will continue to set a renewalMonths value on "buy it" plans, this buyItMembership flag will help us
   * distinguish between "buy it" plans and long-term prepaid plans where we need to (e.g. frontend clients)
   */
  buyItMembership?: boolean;
  /**
   * How many billing periods are in a term? A term is the default length of time that customers are committed to a
   * subscription. e.g. a subscription with renewalMonths=1, billingPeriodsPerTerm=6 is a subscription that bills
   * once every month, and there are 6 months in a term. So the customer is committed to the subscription for 6 months.
   * e.g. a subscription with renewalMonths=12, billingPeriodsPerTerm=1 is a subscription that bills once every year
   * and there is 1 year in a term. So the customer is committed to the subscription for 1 year.
   */
  billingPeriodsPerTerm: number;
  /**
   * Free trial for *n* days.
   */
  freeTrialDays?: number;
  /**
   * Free trial for *n* calendar months, as Recurly runs month trials. Takes precedence over `freeTrialDays` for trial
   * dates; `freeTrialDays` stays an approximation for display.
   */
  freeTrialMonths?: number;
  billingCadence: BillingCadence;
  fullRate?: string;
  fullRateUnit?: string;

  /** @deprecated use `activationFeePricingInCents` instead */
  activationFeeInCents?: number;
  /** Activation fee pricing in our supported currencies */
  activationFeePricingInCents?: PricingByCurrency;

  /**
   * In case we want to waive/discount the activation fee (e.g. A/B testing), activationFeeInCents will hold
   * the actual amount we want to charge. discountedFromOriginalActivationFeeInCents will define what the original fee
   * for frontend display purposes.
   */
  discountedFromOriginalActivationFeeInCents?: number;

  /**
   * Product IDs of collars that can use this subscription
   */
  canBeActivatedOnProductIds: CollarProductId[];
  /**
   * Product IDs of collars for which this subscription can be bundled with. Exmaple of how this differs from
   * canBeActivatedOnProductIds: when purchasing an S3 upgrade, customers will be buying a subscription that
   * can only be _purchased_ with S3 collars.  However, that subscription will immediately be _activated_ on their S2
   * collar. i.e. canBePurchasedForProductIds = [s3], canBeActivatedOnProductIds = [s2, s3]
   */
  canBePurchasedForProductIds: string[];
  category: ProductCategory.SUBSCRIPTION;

  /**
   * We currently have some plans that aren't selectable in the list of subscription options, but we may try
   * to upsell them to the customer through an interstitial modal if they select a shorter term plan. This upsellOnly
   * flag will be used for that purpose. The plan will still need to include with the ecommerce config but the frontend
   * will use this to know to hide it from the list on the product page.
   */
  upsellOnly?: boolean;

  /**
   * If the user selects this subscription product and attemptUpsellToPlanCode is defined, the frontend should
   * attempt to upsell the user to this plan code instead.
   */
  attemptUpsellToPlanCode?: string;
  /**
   * Some membership options have additional labels added to them on the PDP
   * that indicate some kind of value proposition to users
   */
  valueLabel?: string;

  /**
   * Some membership options come with access to phone support, dubbed "Support+"
   */
  supportPlus?: boolean;

  /*
   * Only certain subscriptions are eligible for a referree free month
   */
  eligibleForRefereeFreeMonth?: boolean;

  /**
   * Flag to determine if this plan is currently only available for A/B tests
   */
  experimentOnly?: boolean;

  /**
   * Specifies which experiment is required for this plan to be available
   */
  requiresExperimentEnrollment?: GrowthBookFeatureName;

  /**
   * Lists which experiments should exclude this product when user is enrolled in them
   */
  excludedFromExperiments?: GrowthBookFeatureName[];

  /**
   * Sold only to carts whose GWP partner promo code resolved to a free-trial program of this many months
   */
  requiresGwpPartnerFreeTrialMonths?: number;

  /**
   * Flag to determine if this plan is eligibile for extension by use of coupon
   */
  eligibleForCouponExtension?: boolean;

  /**
   * The number of  months that internal coupon code can extend the subscription by
   */
  couponExtensionMonths?: number;

  /**
   * If we were to switch to this plan now, this is the amount of credit that would be applied to the switch
   * based on the refund policy of the original plan. Usually this is a prorated amount.
   */
  planSwitchCreditAppliedInCents?: number;

  /**
   * If we were to switch to this plan now, this is how much the user would actually pay today.
   */
  planSwitchAmountChargedInCents?: number;

  overrideSetupFeeSku?: string;
  overrideReactivationSetupFeeSku?: string;
  thirdParty?: ThirdPartyId;

  /**
   * Flag to indicate if this is a Plus plan (Mini+ with behavior detection)
   */
  isPlusPlan?: boolean;

  /**
   * The multi-dog discount coupon code that should be applied when 2+ subscriptions
   * of this type are active/purchased together
   */
  multiDogCouponCode?: string;

  /**
   * Alternate pricing for analytics/marketing pixels (e.g., Meta, Northbeam, TikTok).
   * Represents expected lifetime value rather than literal billing price, by currency.
   * When present, external analytics destinations should use this instead of pricingInCents.
   * Internal analytics (firehose) should continue using pricingInCents.
   *
   * Example formulas by plan type:
   * - Monthlies: activation fee + rate × 12 × 50%
   * - 6-month: price + price × 80%
   * - 1-year / 2-year: straight billing price
   */
  analyticsPricingInCents?: PricingByCurrency;
}

export interface IEarlyRenewalProduct extends IBaseProduct {
  sku: string;
  physical: false;
  category: ProductCategory.EARLY_RENEWAL;
}

export enum ColorOption {
  Yellow = 'yellow',
  YellowScribble = 'yellow scribble',
  Gray = 'gray',
  GrayScribble = 'gray scribble',
  Blue = 'blue',
  BlueScribble = 'blue scribble',
  Pink = 'pink',
  PinkScribble = 'pink scribble',
  Strava = 'strava',

  // Makers band styles
  LandsharkSupplyBlackBlue = 'Landshark Supply Black/Blue',
  LandsharkSupplyBlackRed = 'Landshark Supply Black/Red',
  MimiGreenPinkReflective = 'Mimi Green Pink Reflective',
  MimiGreenRedBiothane = 'Mimi Green Red Biothane',
  RopeHoundsBubblegumPop = 'Rope Hounds Bubblegum Pop',
  RopeHoundsMountainTop = 'Rope Hounds Mountain Top',
  StuntPuppyFishtailTeal = 'Stunt Puppy Fishtail Teal',
  StuntPuppyFloraFrolic = 'Stunt Puppy Flora Frolic',
  StuntPuppyGoDog = 'Stunt Puppy Go Dog',
  StuntPuppyPinesBlue = 'Stunt Puppy Pines Blue',
  StuntPuppyTealBiothane = 'Stunt Puppy Teal Biothane',
  ZeeDogPhantom = 'ZeeDog Phantom',
}

export enum SizeOption {
  XSmall = 'xsmall',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  XLarge = 'xlarge',
}

/**
 * Options for buying a collar kit with a deferred subscription, meaning we aren't immediately creating a subscription
 * under the purchaser's account and instead the subscription will be created under a recipient's account at a later
 * date. Another use case for this might be collar kits that are purchased from resellers but we need to create a
 * subscription under the customer's account when they activate their collar in the Fi app.
 */
export interface DeferredSubscriptionOption {
  giftWrapped?: boolean;
  renewalMonths: number;
  /**
   * @deprecated Use `pricingInCents` instead.
   */
  priceInCents: number;
  pricingInCents: PricingByCurrency;
}

// Providing one but not the other indicates unbounded in that direction (eg. {max: 20} means 20 lbs or less)
interface WeightRange {
  min?: number;
  max?: number;
}

export enum BandSeries {
  // The F1 band is compatible with Series 1 and Series 2 collars
  F1 = 'F1',
  // The F3 band is compatible with Series 3 collars
  F3 = 'F3',
}

export enum CollarTypeOption {
  Standard = 'standard',
  Martingale = 'martingale',
}

export interface IVariantOptions {
  color: ColorOption;
  size: SizeOption;
  collarType: CollarTypeOption;
}

export interface IBaseEcommerceVariantConfig {
  variant: string;
  /** Event to fire on page load */
  event: string;
}

export interface IECommerceSiteConfig {
  // Entries here should be optional with the frontend choosing a sane default, so
  // that we don't have to worry about versioning this between frontend/backend.
  showCoupons?: boolean;
  showApplePay?: boolean;
  enableGiftCards?: boolean;
  blockedOutOfStockSkus?: string[];
  /**
   * Physical supplement SKUs (the SKU that ships, not the subscription plan code) we can't ship
   * right now. Storefront and in-app supplement pickers hide them; purchases are rejected anyway.
   */
  backorderedSupplementSkus?: string[];
  disableExpeditedShippingSkus?: string[];
  miniShippingBlockedUsStates?: string[];
  shippingOnlyPartnerParentCoupons?: string[];
  allowPaypalCredit?: boolean;
  isReseller?: boolean;
  shouldResetAnonymousId?: boolean;
  showThankYouPageSurvey?: boolean;
  referreeFreeMonth?: boolean;
  multiDogDiscountAmount?: number;
  multiDogHouseholdDiscountEnabled?: boolean;
  showInAppCancellationChatButton?: boolean;
  showMonthToMonthPauseMembership?: boolean;
  enableEcommPromoBannerImprovements?: boolean;
  showMetalCollarCircleImages?: boolean;
  collarTariffFeeActive?: boolean;
  firstBagFree?: boolean;
  internalTester?: boolean;
  gwpRenewalMonths: number;
  gwpFreeTrialMonths: number;
  gwpFreeTrialDisplayValueInCents: number;
  verizonPostCheckoutMessage?: string;
  miniPlusByDefault?: boolean;
  /**
   * When true, password field is hidden during checkout. Users will set their password
   * when they first log into the mobile app via password reset flow.
   */
  passwordlessCheckoutEnabled?: boolean;
  /** the prepaid terms a hardware-first activation can pick, with first-term and renewal prices per currency */
  hardwareFirstMembershipTerms?: HardwareFirstMembershipTerm[];
}

export interface HardwareFirstMembershipTerm {
  renewalMonths: number;
  /** what the term renews at, per currency in minor units */
  listPriceByCurrency: PricingByCurrency;
  /** what the first term costs after the intro coupon, per currency; a currency with no intro is omitted */
  introPriceByCurrency: PricingByCurrency;
}

export type ShippingCode =
  | 'so-free'
  | 'so-free_trial'
  | 'so-2day'
  | 'so-overnight'
  | 'so-priority_mail'
  | 'so-in_person'
  | 'so-dhl_parcel_ground'
  | 'so-dhl_parcel_expedited_max'
  | 'so-dhl_parcel_plus_ground'
  | 'so-dhl_parcel_plus_expedited_max'
  | 'so-ups_ground'
  | 'so-ups_2nd_day_air'
  | 'so-ups_next_day_air'
  | 'so-ups_next_day_air_saturday'
  | 'so-landmark_international_standard'
  | 'so-flat_rate';

/**
 * The shipping code for free ground shipping.
 */
export const FREE_SHIPPING_CODE: ShippingCode = 'so-free';

/**
 * The shipping code for free trial shipping ($12 USD / $12 CAD).
 */
export const FREE_TRIAL_SHIPPING_CODE: ShippingCode = 'so-free_trial';

export const FLAT_RATE_SHIPPING_CODE: ShippingCode = 'so-flat_rate';

/** Orders with a pre-discount product subtotal below this amount (in cents, in the cart's currency) are charged flat rate shipping */
export const FREE_SHIPPING_THRESHOLD_BY_CURRENCY: PricingByCurrency = {
  USD: 9900,
  CAD: 14900,
  GBP: 9900,
  EUR: 9900,
  RON: 54900,
  SEK: 120900,
  DKK: 82900,
  NOK: 127900,
  CHF: 9900,
  ISK: 1591900,
  TRY: 528900,
};

export interface IShippingOption {
  code: ShippingCode;
  name: string;
  displayedCarrier?: string;
  /**
   * Used as a subtitle in Apple Pay.
   *
   * E.g. "Arrives in 5 to 7 days".
   */
  detail: string;
  /** @deprecated use `pricingInCents` instead */
  priceInCents: number;
  /** Pricing in our supported currencies */
  pricingInCents: PricingByCurrency;
  /**
   * Whether the shipping option is an expedited shipping option that needs to be refunded
   * in the event of a backorder situation.
   */
  isExpedited: boolean;
  exchangeOnly?: boolean;
  inPersonEventOnly?: boolean;
  /**
   * If specified, this shipping option is only available to users enrolled in the specified experiment.
   */
  requiresExperimentEnrollment?: GrowthBookFeatureName;
  /**
   * An ISO date. If specified, we should display text that "the package will arrive on <day>"
   * in the checkout flow.
   *
   * This is dynamically calculated.
   */
  arrivesOn?: string;
}

// Calculated by subtracting the spare band weight from the kit weight in:
// https://docs.google.com/spreadsheets/d/1IQozPMbeg-4lVSStsjV3AoLG1HggkvQcMDe5CJ9J_XU/edit#gid=158751532
export const WEIGHT_IN_OUNCES_OF_S3_KIT_WITHOUT_BAND = 11.0053;

// Factory cost provided by ops team via Slack DM. Therefore a link is not available.
// This value is used for customs declarations.
export const FACTORY_COST_IN_DOLLARS_OF_S3_KIT_WITHOUT_BAND = 65;

export interface EcommerceApiErrorExtensions {
  customerMessage?: string;
  /** Used when a failure can be resolved by a follow-up action, such as with 3D Secure requiring additional authentication */
  token?: string;
  /** Front end ids of cart items involved in the error */
  cartItemIds?: string[];
}

export const giftCardCookieName = 'gift_card_redemption_code';
export const fakeRefereeRewardForPricingCouponCode = 'fake_referee_reward_for_pricing';

export function isModuleSubscriptionProduct(product: IProduct): product is ISubscriptionProduct {
  return product.category === ProductCategory.SUBSCRIPTION;
}

export function isSupplementSubscriptionProduct(product: IProduct): product is ISupplementSubscriptionProduct {
  return product.category === ProductCategory.SUPPLEMENT && !isSinglePurchaseSupplementProduct(product);
}

export function isSinglePurchaseSupplementProduct(product: IProduct): product is ISupplementSubscriptionProduct {
  return product.category === ProductCategory.SUPPLEMENT && product.weeksBetweenShipments === 0;
}

export function isCollarKitProduct(product: IProduct): product is ICollarKitProduct {
  return product.category === ProductCategory.COLLAR_KIT;
}

export function isCollarKitVariant(variant: IVariant): variant is ICollarKitVariant {
  return 'bandSku' in variant;
}

export function isMiniProduct(product: IProduct): product is IMiniProduct {
  return product.category === ProductCategory.MINI;
}

export function isUltraProduct(product: IProduct): product is IUltraProduct {
  return product.category === ProductCategory.ULTRA;
}

export function isFreeShippingProduct(product: IProduct): boolean {
  return product.shipsFree === true;
}

export function isBundledSubscriptionRequiredForPurchase(product: IProduct): boolean {
  // NOTE: Ultra products can be bundled with a subscription (for standalone Ultra),
  // but just the hardware can be purchsed to add to their existing smart collar setup.
  if (product.category === ProductCategory.MINI) {
    return true;
  }

  // Legacy collar kits (Series 1 and Series 2) did not require a subscription to purchase.
  return product.category === ProductCategory.COLLAR_KIT && product.bundledSubscriptionRequiredForPurchase;
}

/**
 * Just-in-time (JIT) kitting is when the fulfillment center will put the kit together at time of shipping. In the
 * context of Fi's business, this means putting the size/color band ordered into a pre-assembled box with the module
 * and charging base.
 *
 * Per-SKU eligibility is sourced from `kitComponents` (variant-level for collar kits, product-level otherwise);
 * see `isSkuEligibleForJITKitting`. This product-level helper is retained for legacy callers (RMA / mispack /
 * GraphQL product surface) that key off the product's `baseModuleKitSku` flag.
 */
export function isCollarKitEligibleForJITKitting(product: IProduct): product is ICollarJITKitProduct {
  return isCollarKitProduct(product) && !!product.baseModuleKitSku;
}

/**
 * Checks if a specific SKU within a product is eligible for just-in-time kitting.
 *
 * Eligibility is the presence of a `kitComponents` entry keyed by the shipping SKU. For collar-kit
 * products, any variant's `kitComponents` map can declare ownership; for single-SKU products (Mini,
 * supplements), eligibility comes from the product-level `kitComponents`.
 */
export function isSkuEligibleForJITKitting(product: IProduct, sku: string): boolean {
  const upperSku = sku.toUpperCase();
  if (isCollarKitProduct(product)) {
    if (product.variants.some((v) => v.kitComponents?.[upperSku])) {
      return true;
    }
  }

  return !!product.kitComponents?.[upperSku];
}

/**
 * Returns true if the SKU is an international variant: `F3C-` for S3+ hardware, `M1B-` for Mini.
 *
 * These are the SKUs that ship outside the US (and to US territories) and that DCL stocks as
 * separate inventory from their domestic counterparts.
 */
export function isInternationalSku(sku: string): boolean {
  return sku.startsWith('F3C-') || sku.startsWith('M1B-');
}

// Mirrors REFURB_SUFFIX_PATTERN in lib/ecommerce/products/refurbSku.ts — this file is copied
// verbatim to the ecommerce repo and must not import it.
const REFURB_SKU_SUFFIX = /-R[ABC]$/i;

/**
 * Finds a band variant by SKU. Matches both the variant's own SKU and any of its fulfillment-rule output SKUs
 * (so e.g. an F3C-prefixed shipping SKU resolves back to its F3B-prefixed customer-facing variant).
 * Refurb-graded SKUs (`-RA`/`-RB`/`-RC`) resolve to the base variant — variants only register base SKUs.
 */
export function findBandVariantBySku(product: IBandProduct, sku: string): IBandVariant | undefined {
  const baseSku = sku.replace(REFURB_SKU_SUFFIX, '');
  return product.variants.find((v) => v.sku === baseSku || v.fulfillmentRules?.some((r) => r.output.sku === baseSku));
}

/**
 * Finds a collar kit variant by SKU. Matches the variant's own SKU or any of its fulfillment-rule output
 * SKUs (so e.g. an F3C-prefixed shipping SKU or a gift-wrap kit SKU resolves back to its customer-facing
 * variant).
 * Refurb-graded SKUs (`-RA`/`-RB`/`-RC`) resolve to the base variant — variants only register base SKUs.
 */
export function findCollarKitVariantBySku(product: ICollarKitProduct, sku: string): ICollarKitVariant | undefined {
  const baseSku = sku.replace(REFURB_SKU_SUFFIX, '');
  return product.variants.find((v) => v.sku === baseSku || v.fulfillmentRules?.some((r) => r.output.sku === baseSku));
}

/**
 * Finds a collar kit or band variant by SKU. Useful for a less verbose way to find a variant when we don't care
 * about the type of product.
 */
export function findCollarKitOrBandVariantBySku(
  product: ICollarKitProduct | IBandProduct,
  sku: string,
): ICollarKitVariant | IBandVariant | undefined {
  if (isCollarKitProduct(product)) {
    return findCollarKitVariantBySku(product, sku);
  }

  return findBandVariantBySku(product, sku);
}
